import decode from 'jwt-decode';
import { abortSignalTimeout, getCommonHeaders, getFullUrl } from 'redux/helpers/utils';
import tokenService from 'services/tokenService';

const fetchNewTokenUrl = getFullUrl('/auth/refresh');

// Bound the refresh so a stalled fetch can't pin the refresh lock forever.
// An abort is standardised to "Failed to fetch" and treated as a transient failure.
// Keep below ensureValidToken's lock-acquisition timeout (30s) so the winning tab
// releases the lock before other tabs time out waiting for it and log out.
const fetchTimeoutMs = 25 * 1000;

const fetchNewToken = async () => {
  const [refreshToken, fcmToken, apnsToken] = await Promise.all([
    tokenService.getRefreshToken(),
    tokenService.getFcmToken(),
    tokenService.getApnsToken(),
  ]);

  return fetch(fetchNewTokenUrl, {
    method: 'POST',
    priority: 'high',
    mode: 'cors',
    signal: abortSignalTimeout(fetchTimeoutMs),
    headers: {
      'Content-Type': 'application/json',
      ...getCommonHeaders(refreshToken),
    },
    ...((fcmToken || apnsToken) && {
      body: JSON.stringify({
        ...(fcmToken && { fcm_token: fcmToken }),
        ...(apnsToken && { apns_token: apnsToken }),
      }),
    }),
  })
    .catch(() => {
      // Standardise all failed to reach server errors
      throw new TypeError('Failed to fetch');
    })
    .then(response => response.json())
    .then(({ data }) => {
      if (!data?.token) {
        throw new Error('Token refresh failed');
      }
      const { token, ...rest } = data;
      return Promise.all([
        tokenService.setAccessToken(token),
        tokenService.setRefreshToken(token),
      ])
        .then(() => rest);
    });
};

const getTokenExpiration = (token) => {
  if (!token) {
    return null;
  }

  try {
    return decode(token).exp;
  } catch (e) {
    return null;
  }
};

const isTokenParsable = (token) => {
  return !!getTokenExpiration(token);
};

const isRefreshable = async () => {
  const refreshToken = await tokenService.getRefreshToken();

  // No point in checking if isTokenValid as the legacyProvider
  // has only a single access token exp claim on the jwt
  return refreshToken && isTokenParsable(refreshToken);
};

export default {
  fetchNewToken,
  isRefreshable,
};
